// import node module libraries
import { Fragment, useEffect, useState } from 'react';
import { Row, Col, Container, Form } from 'react-bootstrap';
// import { Link } from 'react-router-dom';
import axios from 'axios';

// Import authentication components
import useAuthHeader from 'react-auth-kit/hooks/useAuthHeader';

// import translation hook
import { useTranslation } from 'react-i18next';

// Import Loading Animation
import Skeleton from 'react-loading-skeleton'
import 'react-loading-skeleton/dist/skeleton.css'

// import sub components
import ComparePlanPricingCard from './ComparePlanPricingCard';

const PricingPlans = ( { currentPlan }) => {
	const { t } = useTranslation(); // useTranslation-Hook

	const [PlansData, setPlansData] = useState([]);
	const [isLoading, setIsLoading] = useState(true);
	const [billingInterval, setBillingInterval] = useState('year');

	const authHeader = useAuthHeader();
	// fetch data from api
	const options = {
		params: {

		},
		headers: {
			Authorization: authHeader,
			Accept: 'application/json'
		}
	};

	const getPlansData = async () => {
		setIsLoading(true);

		// Dynamische URL basierend auf der Umgebung
		const baseURL = process.env.REACT_APP_ENV === 'development'
			? `http://${process.env.REACT_APP_BACKEND_API_BASE_URI_ENV}`
			: `https://${process.env.REACT_APP_BACKEND_API_BASE_URI_ENV}`;

		await axios.get(`${baseURL}/public/plans?type=platform`, options)
			.then(response => {
				const responseData = response.data.data; // Extract the data from the response
				setPlansData(responseData);
				setIsLoading(false);
			})
			.catch(() => {
				setIsLoading(false);
			});
	}

	// Filter plans based on billing interval
	const getFilteredPlans = () => {
		if (!PlansData || PlansData.length === 0) return [];

		// Group plans by product_name
		const groupedPlans = PlansData.reduce((acc, plan) => {
			if (!acc[plan.product_name]) {
				acc[plan.product_name] = {};
			}
			acc[plan.product_name][plan.interval] = plan;
			return acc;
		}, {});

		// Return plans for selected interval, fallback to month if year not available
		const filteredPlans = Object.values(groupedPlans).map(productPlans => {
			return productPlans[billingInterval] || productPlans['month'];
		}).filter(Boolean);

		// Sort plans by amount (price) in ascending order
		return filteredPlans.sort((a, b) => a.amount - b.amount);
	};

	// Get plans for comparison table (only monthly plans)
	const getComparisonPlans = () => {
		if (!PlansData || PlansData.length === 0) return [];

		// Filter only monthly plans and sort by price
		const yearlyPlans = PlansData
			.filter(plan => plan.interval === 'year')
			.sort((a, b) => a.amount - b.amount);

		return yearlyPlans;
	};

	// Categorize features into groups
	const categorizeFeatures = (features) => {
		const categories = {
			'Flows': [],
			'Benutzer': [],
			'Statistik': [],
			'Support': [],
			'Extras': []
		};

		features.forEach(feature => {
			const lowerFeature = feature.toLowerCase();
			if (lowerFeature.includes('flow') || lowerFeature.includes('ausführung')) {
				categories['Flows'].push(feature);
			} else if (lowerFeature.includes('benutzer') || lowerFeature.includes('user') || lowerFeature.includes('zugang für')) {
				categories['Benutzer'].push(feature);
			} else if (lowerFeature.includes('statistik') || lowerFeature.includes('grafisch')) {
				categories['Statistik'].push(feature);
			} else if (lowerFeature.includes('support') || lowerFeature.includes('help center')) {
				categories['Support'].push(feature);
			} else {
				categories['Extras'].push(feature);
			}
		});

		return categories;
	};

	// Get all unique features across all plans
	const getAllUniqueFeatures = () => {
		const comparisonPlans = getComparisonPlans();
		const allFeatures = new Set();

		comparisonPlans.forEach(plan => {
			plan.marketing_features?.forEach(feature => {
				// Normalisiere den Feature-Namen um Duplikate zu vermeiden
				const normalizedName = feature.name.trim().replace(/\s+/g, ' ');
				allFeatures.add(normalizedName);
			});
		});

		const featuresArray = Array.from(allFeatures).sort();
		return categorizeFeatures(featuresArray);
	};

	// Check if a plan has a specific feature
	const planHasFeature = (plan, featureName) => {
		return plan.marketing_features?.some(feature => {
			const normalizedFeatureName = feature.name.trim().replace(/\s+/g, ' ');
			return normalizedFeatureName === featureName;
		}) || false;
	};

	// Get plan title for display
	const getDisplayTitle = (productName) => {
		const titles = {
			'haeppy.platform.free': 'platform.free',
			'haeppy.platform.starter': 'platform.starter',
			'haeppy.platform.professional': 'platform.professional',
			'haeppy.platform.enterprise': 'platform.enterprise'
		};
		return titles[productName] || productName;
	};

	useEffect(() => {
		getPlansData();
	}, []);

	return (
		<>
		<section className="py-14 bg-white">
			<Container>
				<Row>
					<Col lg={{ span: 8, offset: 2 }} xl={{ span: 8, offset: 2 }} xs={12}>
						{/* heading */}
						<div className=" text-center mb-8">
							<h1 className="display-3 mb-3 fw-bold">
								{t('The best plans for your E-Commerce business')}
							</h1>
							<p className="lead px-md-14">
								{t('We offer three plans to suit your business needs, whether you are just starting out or looking to scale.')}
							</p>
						</div>

						{/* Billing Toggle */}
						<div className="text-center mb-6">
							<div className="d-inline-flex align-items-center p-1 bg-light rounded-pill">
								<span className={`px-3 py-2 rounded-pill fw-medium ${billingInterval === 'month' ? 'bg-white text-primary shadow-sm' : 'text-muted'}`}>
									{t('Monthly')}
								</span>
								<Form.Check
									type="switch"
									id="billing-switch"
									className="mx-2"
									checked={billingInterval === 'year'}
									onChange={(e) => setBillingInterval(e.target.checked ? 'year' : 'month')}
								/>
								<span className={`px-3 py-2 rounded-pill fw-medium ${billingInterval === 'year' ? 'bg-white text-primary shadow-sm' : 'text-muted'}`}>
									{t('Yearly')}
									{(() => {
										// Berechne die maximale prozentuale Ersparnis zwischen Monat und Jahr
										const filteredPlans = getFilteredPlans();
										let percentSaved = null;
										if (filteredPlans.length > 0) {
											const plan = filteredPlans.reduce((max, p) => (p.amount > (max?.amount ?? 0) ? p : max), null); // Finde den Plan mit dem höchsten Betrag
											const allPlans = PlansData.filter(p => p.product_name === plan.product_name);
											const monthly = allPlans.find(p => p.interval === 'month');
											const yearly = allPlans.find(p => p.interval === 'year');
											if (monthly && yearly) {
												const monthlyTotal = monthly.amount * 12;
												const yearlyTotal = yearly.amount;
												if (monthlyTotal > 0) {
													percentSaved = Math.round((1 - yearlyTotal / monthlyTotal) * 100);
												}
											}
										}
										return percentSaved && percentSaved > 0 ? (
											<span className="badge bg-success ms-2 small">
												{t('Saving')} {percentSaved}% 
											</span>
										) : (
											<span className="badge bg-success ms-2 small">
												{t('Saving')}
											</span>
										);
									})()}
								</span>
							</div>
						</div>
					</Col>
					{(isLoading || (PlansData && PlansData.length === 0)) ? (
						<>
							{[1, 2, 3].map((_, idx) => (
								<Col lg={4} md={12} xs={12} key={`loading-${idx}`}>
									<Skeleton className="rounded" height="600px" count={1} />
								</Col>
							))}
						</>
					) : (
						getFilteredPlans().length > 0 && (
							<>
								{getFilteredPlans().map((item, index) => {
									return (
										<Col lg={4} md={12} xs={12} key={index}>
											<ComparePlanPricingCard content={item} currentPlan={currentPlan} />
										</Col>
									);
								})}
							</>
						)
					)}
				</Row>
			</Container>
		</section>
		<section className="pb-14 pt-lg-8 bg-white">
			<Container>
				<Row>
					{/* heading */}
					<Col lg={6} xs={12}>
						<div className="mb-8">
							<h1 className="display-4 fw-bold mb-4 pe-8">
								{t('Compare plans')} & <br />
								<span className="text-primary">{t('discover additional features')}</span>
							</h1>
							{/* para */}
							<p className="lead">
								{t('We offer a variety of additional features to enhance your experience with our platform.')}
							</p>
						</div>
					</Col>
				</Row>
					<Row>
						<Col xs={12}>
							{(isLoading || (PlansData && PlansData.length === 0)) ? (
								<>
									<Skeleton className="rounded" height="40px" count={30} />
								</>
							) : (
								<>
									{/* table */ }
									<div className="table-responsive">
										<table className="table text-nowrap">
											<thead>
												<tr>
													<th className="border-top-0 ps-0" style={{ width: '40%' }}>
														<h1 className="fw-bold">Features</h1>
													</th>
													{getComparisonPlans().map((plan, index) => (
														<th key={index} className="border-top-0 ps-0 text-center">
															<h3 className="mb-1 fw-bold">{getDisplayTitle(plan.product_name)}</h3>
															<div className="fw-normal text-muted">
																{plan.amount === 0 ? 'Kostenlos' : `${(plan.amount / 100 / 12).toFixed(2).replace('.', ',')}€/Monat`}
															</div>
														</th>
													))}
												</tr>
											</thead>
											<tbody>
												{Object.entries(getAllUniqueFeatures()).map(([category, features]) => (
													<Fragment key={category}>
														{features.length > 0 && (
															<>
																<tr>

																	<td colSpan={getComparisonPlans().length + 1} className="border-top-0 pt-8 ps-0">
																		<h3>{category}</h3>
																	</td>
																</tr>
																{features.map((feature, featureIndex) => (
																	<tr key={`${category}-${featureIndex}`}>
																		<td className="ps-0">
																			<span className="text-muted">{feature}</span>
																		</td>
																		{getComparisonPlans().map((plan, planIndex) => (
																			<td key={planIndex} className="text-center">
																				{planHasFeature(plan, feature) ? (
																					<i className="fe fe-check fs-3 text-success"></i>
																				) : (
																					<i className="fe fe-x fs-3 text-muted"></i>
																				)}
																			</td>
																		))}
																	</tr>
																))}
															</>
														)}
													</Fragment>
												))}
											</tbody>
										</table>
									</div>
								</>
							)}
						</Col>
					</Row>
			</Container>
		</section>
		</>
	);
};
export default PricingPlans;